import {computed, Injectable, signal} from '@angular/core';
import {Theme} from '../models/common/theme.constant';

/**
 * Service for managing application theme (light/dark mode).
 * Handles theme switching, persistence, and system preference detection.
 *
 * @description
 * The ThemeService provides:
 * - Signal-based reactive theme state
 * - localStorage persistence of user preference
 * - System theme detection (prefers-color-scheme)
 * - Automatic DOM class application
 *
 * @example
 * Basic usage:
 * ```typescript
 * private readonly themeService = inject(ThemeService);
 *
 * // Initialize theme on app startup
 * ngOnInit() {
 *   this.themeService.initializeTheme();
 * }
 *
 * // Get current theme
 * const currentTheme = this.themeService.theme();
 *
 * // Toggle theme
 * toggleDarkMode() {
 *   this.themeService.toggleTheme();
 * }
 * ```
 *
 * @example
 * Set specific theme:
 * ```typescript
 * // Force light mode
 * this.themeService.setTheme(Theme.LIGHT);
 *
 * // Force dark mode
 * this.themeService.setTheme(Theme.DARK);
 * ```
 *
 * @example
 * React to theme changes in components:
 * ```typescript
 * readonly isDarkMode = computed(() => this.themeService.theme() === Theme.DARK);
 *
 * // Or use effect
 * constructor() {
 *   effect(() => {
 *     const theme = this.themeService.theme();
 *     console.log('Theme changed to:', theme);
 *   });
 * }
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  /** LocalStorage key for persisting theme preference */
  private readonly THEME_KEY = 'app-theme';

  /**
   * Reactive signal containing the current theme.
   * Updates automatically when theme changes.
   */
  theme = signal<Theme>(this.getInitialTheme());
  themeString = computed(() => this.theme() === Theme.DARK ? 'dark' : 'light');

  /**
   * Determines the initial theme on service creation.
   * Priority: saved preference > system preference > light mode (default)
   *
   * @private
   * @returns The initial theme to use
   */
  private getInitialTheme(): Theme {
    const savedTheme = localStorage.getItem(this.THEME_KEY) as Theme;
    if (savedTheme) {
      return savedTheme;
    }

    if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return Theme.DARK;
    }

    return Theme.LIGHT;
  }

  /**
   * Toggles between light and dark themes.
   * Automatically saves the new preference and applies it to the DOM.
   *
   * @example
   * ```typescript
   * // In a theme toggle button component
   * onToggleClick() {
   *   this.themeService.toggleTheme();
   * }
   * ```
   */
  toggleTheme(): void {
    const newTheme = this.theme() === Theme.LIGHT ? Theme.DARK : Theme.LIGHT;
    this.setTheme(newTheme);
  }

  /**
   * Sets a specific theme.
   * Updates the signal, persists to localStorage, and applies DOM changes.
   *
   * @param theme - The theme to set (Theme.LIGHT or Theme.DARK)
   *
   * @example
   * ```typescript
   * // Force dark mode for a specific page
   * ngOnInit() {
   *   this.themeService.setTheme(Theme.DARK);
   * }
   *
   * ngOnDestroy() {
   *   // Restore to user preference or light mode
   *   this.themeService.setTheme(Theme.LIGHT);
   * }
   * ```
   */
  setTheme(theme: Theme): void {
    this.theme.set(theme);
    localStorage.setItem(this.THEME_KEY, theme);
    this.applyTheme(theme);
  }

  /**
   * Applies the theme by adding/removing CSS classes on the document root element.
   * This allows Tailwind's dark mode (class strategy) to work correctly.
   *
   * @private
   * @param theme - The theme to apply
   */
  private applyTheme(theme: Theme): void {
    const root = document.documentElement;

    if (theme === Theme.DARK) {
      root.classList.add(Theme.DARK);
    } else {
      root.classList.remove(Theme.DARK);
    }
  }

  /**
   * Initializes the theme by applying the current theme to the DOM.
   * Should be called once during application bootstrap.
   *
   * @example
   * ```typescript
   * // In app.component.ts or main app initialization
   * export class AppComponent implements OnInit {
   *   private readonly themeService = inject(ThemeService);
   *
   *   ngOnInit() {
   *     this.themeService.initializeTheme();
   *   }
   * }
   * ```
   */
  initializeTheme(): void {
    this.applyTheme(this.theme());
  }
}
